
import type { DragonSouls, DragonSoulRefined, SoulSlot } from "../net/messages";
import type { Inventory } from "../net/connection";
import { itemLink } from "../itemfacts";
import { CANNOT_SELL, NO, YES, sellQuestion } from "./bag";
import { elem, installChrome, styleInstaller } from "./chrome";
import { itemIconNode } from "./itemicon";

export type { DragonSouls, DragonSoulRefined, SoulSlot };

export interface DragonSoulActions {
  /** Move a soul between two cells of the server's own address space. */
  onMove?: (source: number, dest: number) => void;
  /** Switch a deck on. There is no "off". */
  onDeck?: (deck: number) => void;
  /** Improve. `souls` are window cells, `stones` are bag slots. */
  onRefine?: (
    operation: number,
    souls: readonly number[],
    stones: readonly number[],
  ) => void;
  onUseFromBag?: (slot: number, dest: number) => void;
  /** Sell one stored soul at the counter this window's sell mode names.
   *  `SendShopSellPacketNew(cell, count, DRAGON_SOUL_INVENTORY)`
   *  (`uidragonsoul.py:418`) -- `cell` here is the storage cell, which the
   *  caller offsets by `DRAGON_SOUL_SLOT_BASE` on the wire. */
  onSell?: (npcId: number, cell: number, count: number) => void;
  /** `ime.PasteString(player.GetItemLink(...))` (`uidragonsoul.py:370-372`). */
  onItemLink?: (link: string) => void;
  onCancel?: () => void;
}

/** `dsinventory.CAPACITY` -- where the storage window ends and the deck begins.
 *  `DRAGON_SOUL_INVENTORY_MAX_NUM` (`item_length.h:152-155`). */
/** The Alchemist's refinement row, `dragon_soul_refine._010_npcChat` with its
 *  trailing space trimmed (`translate_en.lua:831`, `dragon_soul_refine.quest:4`). */
export const DS_REFINE_CHOICE = "Dragon Stone Refinement";

export const DECK_BASE = 960;

/** `DS_SLOT_MAX` (`item_length.h:127`): cells in one deck. */
export const DECK_SIZE = 6;

export const DRAGON_SOUL_SLOT_BASE = 100_000;

/** `DRAGON_SOUL_DECK_MAX_NUM` (`length.h:140`). */
export const DECK_COUNT = 2;

/** The six storage kinds in `DragonSoulWindow.KIND_TAP_TITLES`
 * (`uidragonsoul.py:29-31`), in the same vnum order 11 through 16. */
export const SOUL_KIND_TITLES = [
  "Dragon Diamond", "Dragon Ruby", "Dragon Jade", "Dragon Sapphire",
  "Dragon Garnet", "Dragon Onyx",
] as const;

export const GRADE_TITLES = ["Rough", "Cut", "Rare", "Antique", "Legendary"] as const;

/** `DRAGON_SOUL_BOX_SIZE` (`length.h:58`): cells in one (type, grade) box. */
export const BOX_SIZE = 32;

/** `ItemSlot.x_count` (`dragonsoulwindow.py`): columns in the storage grid. */
export const STORAGE_GRID_COLUMNS = 8;
/** `ItemSlot.y_count` (`dragonsoulwindow.py`): rows in the storage grid. */
export const STORAGE_GRID_ROWS = 4;
/** `ItemSlot` cell count: 8 × 4 (`dragonsoulwindow.py` `grid_table`). */
export const STORAGE_GRID_CELLS = STORAGE_GRID_COLUMNS * STORAGE_GRID_ROWS;

/** `RefineSlot.x_count` (`dragonsoulrefinewindow.py`). */
export const REFINE_SLOT_COLUMNS = 5;
/** `RefineSlot.y_count` (`dragonsoulrefinewindow.py`). */
export const REFINE_SLOT_ROWS = 3;
/** `RefineSlot` cell count: 5 × 3. */
export const REFINE_SLOT_CELLS = REFINE_SLOT_COLUMNS * REFINE_SLOT_ROWS;

/** `ResultSlot.x_count` (`dragonsoulrefinewindow.py`). */
export const RESULT_SLOT_COLUMNS = 2;
/** `ResultSlot.y_count` (`dragonsoulrefinewindow.py`). */
export const RESULT_SLOT_ROWS = 3;
/** `ResultSlot` cell count: 2 × 3. */
export const RESULT_SLOT_CELLS = RESULT_SLOT_COLUMNS * RESULT_SLOT_ROWS;

/** `ItemSlot`/`RefineSlot`/`ResultSlot` `x_step`/`y_step` (32). */
export const DRAGONSOUL_SLOT_SIZE = 32;

export const STEP_MAX = 5;

export function boxStart(soul: SoulSlot): number {
  const subType = Math.floor(soul.vnum / 10000) - 11;
  return subType * STEP_MAX * BOX_SIZE + soul.grade * BOX_SIZE;
}

/** `DRAGON_SOUL_EXPIRED` (`locale_en/locale_game.txt`). */
const DRAGON_SOUL_EXPIRED = "Expired Dragon Stone";
/** `DRAGON_SOUL_EQUIP_WARNING1` and `2` (`locale_en/locale_game.txt`), the
 *  two lines `__OpenQuestionDialog` sets (`uidragonsoul.py:605-607`). */
const DRAGON_SOUL_EQUIP_WARNING1 = "It is difficult to remove a Dragon Stone once it is attached.";
const DRAGON_SOUL_EQUIP_WARNING2 = "Would you still like to continue?";

export function deckCell(soul: SoulSlot, deck: number): number {
  const subType = Math.floor(soul.vnum / 10000) - 11;
  return DECK_BASE + deck * DECK_SIZE + subType;
}

/** `DragonSouls.active_deck` when there is none. Not zero, which is deck 0. */
export const NO_DECK = 255;

/** `DragonSoulRefine.operation`, matching `net/schema.py`'s `DS_REFINE_*`. */
export const REFINE_GRADE = 0;
export const REFINE_STEP = 1;
export const REFINE_STRENGTH = 2;

/** `net/schema.py`'s `DS_REFINE_CLOSE`: closes the refine window server-side
 *  (`DS_SUB_HEADER_CLOSE`, `char_dragonsoul.cpp:134-137`) carried over this
 *  same message rather than a fourth opcode. */
export const REFINE_CLOSE = 255;

/** The legacy DragonSoulWindow board dimensions (`dragonsoulwindow.py:8-31`). */
export const DRAGONSOUL_WINDOW_WIDTH = 287;
export const DRAGONSOUL_WINDOW_HEIGHT = 505;
/** Legacy placement: x = SCREEN_WIDTH - 176 - 287 - 10 and
 * y = SCREEN_HEIGHT - 37 - 505 (`dragonsoulwindow.py:5-11`). */
export const DRAGONSOUL_RIGHT_INSET = 186;
export const DRAGONSOUL_BOTTOM_INSET = 37;

/** `DragonSoulRefineWindow`'s own board (`dragonsoulrefinewindow.py:8-16`):
 *  287 wide like the storage window, but only 232 tall. */
export const DRAGONSOUL_REFINE_WINDOW_WIDTH = 287;
export const DRAGONSOUL_REFINE_WINDOW_HEIGHT = 232;

export const DRAGONSOUL_REFINE_RIGHT_INSET = DRAGONSOUL_RIGHT_INSET + DRAGONSOUL_WINDOW_WIDTH;
export const DRAGONSOUL_REFINE_BOTTOM_INSET =
  DRAGONSOUL_BOTTOM_INSET + (DRAGONSOUL_WINDOW_HEIGHT - DRAGONSOUL_REFINE_WINDOW_HEIGHT);

const STYLE_ID = "dragonsoul-style";

const STYLE = `
.dragonsoul-panel {
  /* The source's board is 287x505 and sits beside the inventory at
     right=186px, bottom=37px (dragonsoulwindow.py:5-31). */
  position: fixed;
  left: auto;
  right: 186px;
  top: auto;
  bottom: 37px;
  transform: none;
  z-index: 60;
  width: 287px;
  height: 505px;
  overflow-y: auto;
  padding: 14px 16px;
  border-radius: 12px;
  background: rgba(12, 14, 18, 0.94);
  border: 1px solid rgba(200, 182, 255, 0.24);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  pointer-events: auto;
  font: 13px/1.5 system-ui, sans-serif;
  color: #d8dee6;
}
.dragonsoul-title {
  font-size: 0.68rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8d84a0;
  margin-bottom: 8px;
}
.dragonsoul-section { margin: 10px 0 0; padding: 8px 0 0; border-top: 1px solid rgba(255,255,255,0.08); }
.dragonsoul-section-label {
  font-size: 0.68rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8d84a0;
  margin-bottom: 5px;
}
.dragonsoul-kind-tabs { display: flex; flex-wrap: wrap; gap: 3px; margin-bottom: 6px; }
.dragonsoul-kind-tab {
  padding: 2px 5px; border: 1px solid rgba(255,255,255,0.14); border-radius: 4px;
  background: transparent; color: #aeb5c0; font: 0.72rem/1.3 system-ui,sans-serif;
  cursor: pointer;
}
.dragonsoul-kind-tab[aria-selected="true"] {
  border-color: rgba(200, 182, 255, 0.65); background: rgba(200, 182, 255, 0.15);
  color: #e8e1ff;
}
/* The second, nested tab set (Inventory_Tab_01..05,
   dragonsoulwindow.py:44-186): one grade page inside the selected kind. */
.dragonsoul-grade-tabs { display: flex; flex-wrap: wrap; gap: 3px; margin-bottom: 6px; }
.dragonsoul-grade-tab {
  padding: 2px 5px; border: 1px solid rgba(255,255,255,0.1); border-radius: 4px;
  background: transparent; color: #8d94a0; font: 0.7rem/1.3 system-ui,sans-serif;
  cursor: pointer;
}
.dragonsoul-grade-tab[aria-selected="true"] {
  border-color: rgba(255, 210, 122, 0.6); background: rgba(255, 210, 122, 0.14);
  color: #ffe1ab;
}
.dragonsoul-empty { color: #7c848d; font-style: italic; }
/* Sell mode: the same amber the bag wears while a dealer's counter is open,
   so one glance says a click sells rather than picks. */
.dragonsoul-selling { border-color: rgba(255, 210, 122, 0.55); }
.dragonsoul-selling .dragonsoul-storage-cell[data-occupied="true"] {
  box-shadow: inset 0 0 0 1px rgba(255, 210, 122, 0.45);
}
/* ItemSlot 8x4 (dragonsoulwindow.py): Slot_Base face via .m2-slot. */
.dragonsoul-storage-grid {
  display: grid;
  grid-template-columns: repeat(${STORAGE_GRID_COLUMNS}, ${DRAGONSOUL_SLOT_SIZE}px);
  grid-template-rows: repeat(${STORAGE_GRID_ROWS}, ${DRAGONSOUL_SLOT_SIZE}px);
  width: ${STORAGE_GRID_COLUMNS * DRAGONSOUL_SLOT_SIZE}px;
  height: ${STORAGE_GRID_ROWS * DRAGONSOUL_SLOT_SIZE}px;
  margin: 4px 0 6px;
  gap: 0;
}
.dragonsoul-storage-cell {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: inherit;
  overflow: hidden;
}
.dragonsoul-storage-cell[data-occupied="true"] { cursor: pointer; }
.dragonsoul-storage-cell.dragonsoul-soul-picked {
  outline: 1px solid rgba(200, 182, 255, 0.7);
  outline-offset: -1px;
}
.dragonsoul-storage-cell .item-icon {
  width: 32px;
  height: 32px;
  margin: 0;
  pointer-events: none;
}
.dragonsoul-storage-glyph {
  font: 9px/1 Tahoma, "MS Sans Serif", sans-serif;
  color: #c8b6ff;
  text-shadow: 0 0 2px #000;
  pointer-events: none;
  max-width: 30px;
  overflow: hidden;
  white-space: nowrap;
}
.dragonsoul-soul { padding: 4px 6px; border-radius: 6px; border: 1px solid transparent; }
.dragonsoul-soul-active { border-color: rgba(143, 209, 138, 0.4); }
.dragonsoul-soul-picked { border-color: rgba(200, 182, 255, 0.55); }
.dragonsoul-soul-head { display: flex; justify-content: space-between; gap: 12px; }
.dragonsoul-name { color: #c8b6ff; }
.dragonsoul-applies { padding-left: 8px; font-size: 0.88rem; }
.dragonsoul-apply-basic { color: #b8bec6; }
.dragonsoul-apply-drawn { color: #ffd27a; }
.dragonsoul-row { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px; }
.dragonsoul-row button,
.dragonsoul-buttons button {
  padding: 3px 7px;
  border-radius: 5px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: transparent;
  color: #d8dee6;
  font: inherit;
  cursor: pointer;
}
.dragonsoul-row button:hover:not(:disabled),
.dragonsoul-buttons button:hover:not(:disabled) { background: rgba(255,255,255,0.08); }
.dragonsoul-row button:disabled,
.dragonsoul-buttons button:disabled { opacity: 0.45; cursor: default; }
/* frame.active_deck: this deck is actually switched on server-side, whether
   or not it is the one currently being looked at. */
.dragonsoul-deck-on { border-color: rgba(143, 209, 138, 0.5); color: #8fd18a; }
/* viewingDeck: this deck's six equipment cells are the ones drawn below --
   a purely local choice, matching deckTab[n].Down() in SetDeckPage
   (uidragonsoul.py:198-199,647-658), which sends nothing on its own. */
.dragonsoul-deck-tab[aria-selected="true"] { text-decoration: underline; }
.dragonsoul-buttons { display: flex; gap: 8px; margin-top: 12px; }
.dragonsoul-refine-axis-on { border-color: rgba(255, 210, 122, 0.65); background: rgba(255, 210, 122, 0.15); color: #ffe1ab; }
.dragonsoul-warning {
  margin-top: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  background: rgba(180, 60, 50, 0.18);
  border: 1px solid rgba(224, 115, 106, 0.35);
  color: #f0b8b2;
  font-size: 0.85rem;
}
.dragonsoul-armed-hint {
  margin-top: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  background: rgba(200, 182, 255, 0.12);
  border: 1px solid rgba(200, 182, 255, 0.32);
  color: #c8b6ff;
  font-size: 0.85rem;
}
/* The offer, tinted like the hint above it so the two read as one gesture
   crossing two windows rather than as an extra control that appeared. */
.dragonsoul-use-armed { border-color: rgba(200, 182, 255, 0.5) !important; color: #c8b6ff !important; }
.dragonsoul-result { margin-top: 10px; font-size: 0.9rem; }
.dragonsoul-result-good { color: #8fd18a; }
.dragonsoul-result-bad { color: #e0736a; }

/* DragonSoulRefineWindow's own board (dragonsoulrefinewindow.py:1-16):
   287x232, docked to the storage window's left edge, sharing its top edge --
   see DRAGONSOUL_REFINE_RIGHT_INSET / _BOTTOM_INSET for the arithmetic. */
.dragonsoul-refine-panel {
  position: fixed;
  left: auto;
  right: 473px;
  top: auto;
  bottom: 310px;
  transform: none;
  z-index: 61;
  width: 287px;
  height: 232px;
  overflow-y: auto;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(12, 14, 18, 0.94);
  border: 1px solid rgba(200, 182, 255, 0.24);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  pointer-events: auto;
  font: 13px/1.5 system-ui, sans-serif;
  color: #d8dee6;
}
.dragonsoul-refine-axes { display: flex; gap: 6px; margin: 6px 0; }
/* RefineSlot 5x3 and ResultSlot 2x3 (dragonsoulrefinewindow.py), side by
   side the way the script places them at x=15 and x=207. */
.dragonsoul-refine-boards {
  display: flex;
  gap: 16px;
  margin: 4px 0 6px;
  align-items: flex-start;
}
.dragonsoul-refine-grid {
  display: grid;
  grid-template-columns: repeat(${REFINE_SLOT_COLUMNS}, ${DRAGONSOUL_SLOT_SIZE}px);
  grid-template-rows: repeat(${REFINE_SLOT_ROWS}, ${DRAGONSOUL_SLOT_SIZE}px);
  width: ${REFINE_SLOT_COLUMNS * DRAGONSOUL_SLOT_SIZE}px;
  height: ${REFINE_SLOT_ROWS * DRAGONSOUL_SLOT_SIZE}px;
  gap: 0;
}
.dragonsoul-result-grid {
  display: grid;
  grid-template-columns: repeat(${RESULT_SLOT_COLUMNS}, ${DRAGONSOUL_SLOT_SIZE}px);
  grid-template-rows: repeat(${RESULT_SLOT_ROWS}, ${DRAGONSOUL_SLOT_SIZE}px);
  width: ${RESULT_SLOT_COLUMNS * DRAGONSOUL_SLOT_SIZE}px;
  height: ${RESULT_SLOT_ROWS * DRAGONSOUL_SLOT_SIZE}px;
  gap: 0;
}
.dragonsoul-refine-cell,
.dragonsoul-result-cell {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: default;
  color: inherit;
  overflow: hidden;
}
.dragonsoul-refine-cell[data-occupied="true"] { cursor: pointer; }
.dragonsoul-refine-cell .item-icon,
.dragonsoul-result-cell .item-icon {
  width: 32px;
  height: 32px;
  margin: 0;
  pointer-events: none;
}
.dragonsoul-refine-glyph,
.dragonsoul-result-glyph {
  font: 8px/1 Tahoma, "MS Sans Serif", sans-serif;
  color: #ffe1ab;
  text-shadow: 0 0 2px #000;
  pointer-events: none;
  max-width: 30px;
  overflow: hidden;
  white-space: nowrap;
}

/* On a phone the desktop pair of boards cannot fit side-by-side: preserving
   right: 186px puts the 287px soul board beyond the left edge. The constructor
   deliberately writes the desktop anchor inline, so these need !important to
   win only at the narrow breakpoint. Keep every visible control inside the
   safe viewport and let the existing vertical scroll carry the longer list. */
@media (max-width: 620px) {
  .dragonsoul-panel {
    left: max(12px, env(safe-area-inset-left)) !important;
    right: max(12px, env(safe-area-inset-right)) !important;
    top: max(12px, env(safe-area-inset-top)) !important;
    bottom: max(37px, calc(env(safe-area-inset-bottom) + 37px)) !important;
    width: auto !important;
    height: auto !important;
  }
  /* The two legacy windows sat side by side on a 1024px desktop; there is no
     room for that on a phone. Rather than overlap the full-screen storage
     panel above, the refine panel becomes a capped call-out docked to the
     top of the same safe area, over the storage panel's own top edge -- a
     higher z-index and an opaque background keep it legible, and its own
     overflow-y carries a refine grid too tall to fit the 42vh cap. */
  .dragonsoul-refine-panel {
    left: max(12px, env(safe-area-inset-left)) !important;
    right: max(12px, env(safe-area-inset-right)) !important;
    top: max(12px, env(safe-area-inset-top)) !important;
    bottom: auto !important;
    width: auto !important;
    height: auto !important;
    max-height: 42vh;
  }
}
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

export function formatRemaining(seconds: number): string {
  if (seconds <= 0) return "run dry";
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/** Whether a cell number is in the deck half of the address space. */
export function isDeckCell(cell: number): boolean {
  return cell >= DECK_BASE;
}

/** `DS_SLOT1`..`DS_SLOT6` are the vnum's 11..16 type minus eleven
 * (`DragonSoul.cpp:76-79`; `uidragonsoul.py:628-644` selects this index). */
export function soulKind(soul: SoulSlot): number {
  return Math.floor(soul.vnum / 10_000) - 11;
}

export function describeOutcome(result: DragonSoulRefined): string {
  // "Destroyed", the shipped verb for this exact risk
  // (`DRAGON_SOUL_UNEQUIP_WARNING1`, `locale_game.txt:91`), not a house word.
  if (result.destroyed) return `${result.was_name} destroyed.`;
  if (result.succeeded) return `${result.was_name} became ${result.result_name}.`;
  if (result.result_name) return `It failed. ${result.was_name} slipped back to ${result.result_name}.`;
  return `It failed. ${result.was_name} is unchanged.`;
}

export class DragonSoulPanel {
  private readonly root: HTMLElement;
  private readonly stored: HTMLElement;
  /** `ItemSlot` 8×4 (`dragonsoulwindow.py`), drawn beside the kept list. */
  private readonly storageGrid: HTMLElement;
  private readonly storageCells: HTMLButtonElement[] = [];
  private readonly deckRow: HTMLElement;
  private readonly deckButtons: HTMLElement;
  private readonly activateButton: HTMLButtonElement;
  private readonly kindTabs: HTMLElement;
  private readonly gradeTabs: HTMLElement;
  private readonly warning: HTMLElement;
  private readonly armedHint: HTMLElement;
  private readonly result: HTMLElement;
  /** `DragonSoulRefineWindow`, a second window in the original -- see the
   *  module docstring. */
  private readonly refineRoot: HTMLElement;
  private readonly grid: HTMLElement;
  /** `RefineSlot` 5×3 (`dragonsoulrefinewindow.py`). */
  private readonly refineGrid: HTMLElement;
  private readonly refineCells: HTMLButtonElement[] = [];
  /** `ResultSlot` 2×3 (`dragonsoulrefinewindow.py`). */
  private readonly resultGrid: HTMLElement;
  private readonly resultCells: HTMLButtonElement[] = [];
  private readonly stones: HTMLElement;
  private readonly stoneLabel: HTMLElement;
  private readonly refineAxisButtons: Array<{ button: HTMLButtonElement; operation: number }> = [];
  private readonly doRefineButton: HTMLButtonElement;
  private frame: DragonSouls | null = null;
  private bag: Inventory | null = null;
  /** Last settled refine outcome name, painted into `ResultSlot` until close. */
  private resultLabel: string | null = null;
  /** The native window starts on kind zero (`uidragonsoul.py:39`). */
  private kind = 0;
  /** `self.inventoryPageIndex = 0` (`uidragonsoul.py:44`): the grade page
   *  inside the current kind tab -- "Rough" first. */
  private gradePage = 0;
  /** `self.deckPageIndex = 0` (`uidragonsoul.py:43`): which deck's six
   *  equipment cells are on screen. Purely local -- see `showDeck`. */
  private viewingDeck = 0;
  /** `self.currentRefineType = self.REFINE_TYPE_GRADE`
   *  (`dragonsoulrefinewindow.py`'s `Show`, resetting on every open). */
  private refineType: number = REFINE_GRADE;
  private picked: number[] = [];
  private stone: number | null = null;
  /** The bag cell the inventory says is armed, and the name to put on the
   *  button. Null between gestures. Told, never discovered -- see
   *  `armBagSlot`. */
  private armed: { slot: number; name: string } | null = null;
  /** The dealer this window is selling to, or null in the ordinary window.
   *  `app.SELL == app.GetCursor()` is the whole of the original's test
   *  (`uidragonsoul.py:364-367`); the cursor is a mode the shop panel sets. */
  private sell: { npcId: number; dealerName: string } | null = null;
  private readonly askEl: HTMLElement;
  private readonly askText: HTMLElement;
  private readonly askYes: HTMLButtonElement;
  private pendingAsk: (() => void) | null = null;

  constructor(
    container: HTMLElement,
    private readonly actions: DragonSoulActions = {},
  ) {
    const doc = container.ownerDocument;
    installChrome(doc);
    installStyle(doc);

    this.root = elem(doc, "div", "dragonsoul-panel");
    this.root.hidden = true;
    this.root.style.left = "auto";
    this.root.style.right = `${DRAGONSOUL_RIGHT_INSET}px`;
    this.root.style.top = "auto";
    this.root.style.bottom = `${DRAGONSOUL_BOTTOM_INSET}px`;
    this.root.style.transform = "none";

    const title = elem(doc, "div", "dragonsoul-title");
    title.textContent = "Dragon Stone Alchemy";

    this.warning = elem(doc, "div", "dragonsoul-warning");
    this.warning.hidden = true;

    // Shown only while the inventory says a cell is armed. The gesture spans
    // two windows, so the half a player is looking at has to say what the
    // other half started -- otherwise a row that grew an extra button is a row
    // that changed for no visible reason.
    this.armedHint = elem(doc, "div", "dragonsoul-armed-hint");
    this.armedHint.hidden = true;

    const deckSection = elem(doc, "div", "dragonsoul-section");
    const deckLabel = elem(doc, "div", "dragonsoul-section-label", "Worn");
    // Named containers, because the two lists hold rows of the same class and
    // the difference between them decides which refusal a gesture earns: a
    // soul in `dragonsoul-worn` is equipped (`DragonSoul.cpp:1078-1080`) and a
    // soul in `dragonsoul-stored` is not.
    this.deckRow = elem(doc, "div", "dragonsoul-worn");
    this.deckButtons = elem(doc, "div", "dragonsoul-row");
    this.deckButtons.setAttribute("role", "tablist");
    // The original's separate `activateButton` (`dragonsoulwindow.py:279-286`):
    // a single toggle for whichever deck `deckTab` currently has open, not one
    // button per deck. Created once so redraw can update its text in place.
    this.activateButton = doc.createElement("button");
    this.activateButton.type = "button";
    this.activateButton.addEventListener("click", () => this.activateDeck());
    deckSection.append(deckLabel, this.deckRow, this.deckButtons);

    const storeSection = elem(doc, "div", "dragonsoul-section");
    const storeLabel = elem(doc, "div", "dragonsoul-section-label", "Kept");
    this.kindTabs = elem(doc, "div", "dragonsoul-kind-tabs");
    this.kindTabs.setAttribute("role", "tablist");
    for (const [kind, titleText] of SOUL_KIND_TITLES.entries()) {
      const tab = doc.createElement("button");
      tab.type = "button";
      tab.className = "dragonsoul-kind-tab";
      tab.textContent = titleText.replace("Dragon ", "");
      tab.dataset.soulKind = String(kind);
      tab.setAttribute("role", "tab");
      tab.addEventListener("click", () => this.setKind(kind));
      this.kindTabs.append(tab);
    }
    // The nested grade tab set (`Inventory_Tab_01`..`_05`,
    // `dragonsoulwindow.py:44-186`) -- see `GRADE_TITLES`.
    this.gradeTabs = elem(doc, "div", "dragonsoul-grade-tabs");
    this.gradeTabs.setAttribute("role", "tablist");
    for (const [grade, titleText] of GRADE_TITLES.entries()) {
      const tab = doc.createElement("button");
      tab.type = "button";
      tab.className = "dragonsoul-grade-tab";
      tab.textContent = titleText;
      tab.dataset.soulGrade = String(grade);
      tab.setAttribute("role", "tab");
      tab.addEventListener("click", () => this.setGrade(grade));
      this.gradeTabs.append(tab);
    }
    // `ItemSlot` grid_table 8×4 (`dragonsoulwindow.py:179-192`). Local index
    // `i` maps to the server cell the same way
    // `__InventoryLocalSlotPosToGlobalSlotPos` does
    // (`uidragonsoul.py:250-252`): kind×STEP_MAX×BOX_SIZE + grade×BOX_SIZE + i.
    this.storageGrid = elem(doc, "div", "dragonsoul-storage-grid");
    this.storageGrid.setAttribute("role", "grid");
    this.storageGrid.setAttribute("aria-label", "Dragon Stone storage");
    for (let i = 0; i < STORAGE_GRID_CELLS; i += 1) {
      const cell = doc.createElement("button");
      cell.type = "button";
      cell.className = "dragonsoul-storage-cell m2-slot";
      cell.dataset.local = String(i);
      const local = i;
      cell.addEventListener("click", (event) => this.clickStorageCell(local, event));
      cell.addEventListener("contextmenu", (event) => {
        event.preventDefault();
        this.useStorageCell(local);
      });
      this.storageCells.push(cell);
      this.storageGrid.append(cell);
    }
    this.stored = elem(doc, "div", "dragonsoul-stored");
    storeSection.append(storeLabel, this.kindTabs, this.gradeTabs, this.storageGrid, this.stored);

    // The same `uiCommon.QuestionDialog` board the bag asks a sale with, in
    // the same classes: two spellings of one dialog would be two answers to
    // "do you want to sell this".
    this.askEl = elem(doc, "div", "bag-ask");
    this.askEl.hidden = true;
    this.askEl.setAttribute("role", "dialog");
    this.askText = elem(doc, "p", "bag-ask-text");
    this.askYes = doc.createElement("button");
    this.askYes.type = "button";
    this.askYes.className = "bag-ask-yes";
    this.askYes.textContent = YES;
    this.askYes.addEventListener("click", () => this.acceptAsk());
    const askNo = doc.createElement("button");
    askNo.type = "button";
    askNo.className = "bag-ask-no";
    askNo.textContent = NO;
    askNo.addEventListener("click", () => this.closeAsk());
    const askButtons = elem(doc, "div", "bag-ask-buttons");
    askButtons.append(this.askYes, askNo);
    this.askEl.append(this.askText, askButtons);

    this.result = elem(doc, "div", "dragonsoul-result");
    this.result.hidden = true;

    const cancel = doc.createElement("button");
    cancel.type = "button";
    cancel.textContent = "Close";
    cancel.addEventListener("click", () => this.cancel());
    const closeRow = elem(doc, "div", "dragonsoul-buttons");
    closeRow.append(cancel);

    this.root.append(
      title, this.warning, this.armedHint, deckSection, storeSection, closeRow,
      this.result, this.askEl,
    );
    container.append(this.root);
    // `DragonSoulWindow.OnPressEscapeKey` closes the window and returns True
    // (`uidragonsoul.py:301-303`). Keep the visible panel from leaking Escape
    // into world movement, while sharing the same cleanup path as Close.
    this.root.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !this.showing) return;
      event.preventDefault();
      event.stopPropagation();
      this.cancel();
    });

    // `DragonSoulRefineWindow` -- a second window in the original, docked to
    // this one's left; see the module docstring for the geometry citations.
    this.refineRoot = elem(doc, "div", "dragonsoul-refine-panel");
    this.refineRoot.hidden = true;
    this.refineRoot.style.left = "auto";
    this.refineRoot.style.right = `${DRAGONSOUL_REFINE_RIGHT_INSET}px`;
    this.refineRoot.style.top = "auto";
    this.refineRoot.style.bottom = `${DRAGONSOUL_REFINE_BOTTOM_INSET}px`;
    this.refineRoot.style.transform = "none";

    const refineTitle = elem(doc, "div", "dragonsoul-title");
    // `DRAGONSOUL_REFINE_WINDOW_TITLE` (`locale_interface.txt`,
    // `dragonsoulrefinewindow.py`'s `TitleName`).
    refineTitle.textContent = "Refinement Window";

    const axesRow = elem(doc, "div", "dragonsoul-refine-axes");
    axesRow.setAttribute("role", "tablist");
    for (const [label, operation] of [
      ["Class", REFINE_GRADE],
      ["Clarity grade", REFINE_STEP],
      ["Level", REFINE_STRENGTH],
    ] as const) {
      const button = doc.createElement("button");
      button.type = "button";
      button.textContent = label;
      button.setAttribute("role", "tab");
      button.addEventListener("click", () => this.chooseRefineType(operation));
      axesRow.append(button);
      this.refineAxisButtons.push({ button, operation });
    }

    const gridLabel = elem(doc, "div", "dragonsoul-section-label", "On the grid");
    this.grid = elem(doc, "div", "dragonsoul-row");

    // `RefineSlot` 5×3 and `ResultSlot` 2×3 (`dragonsoulrefinewindow.py:67-95`).
    const boards = elem(doc, "div", "dragonsoul-refine-boards");
    this.refineGrid = elem(doc, "div", "dragonsoul-refine-grid");
    this.refineGrid.setAttribute("role", "grid");
    this.refineGrid.setAttribute("aria-label", "Refine slots");
    for (let i = 0; i < REFINE_SLOT_CELLS; i += 1) {
      const cell = doc.createElement("button");
      cell.type = "button";
      cell.className = "dragonsoul-refine-cell m2-slot";
      cell.dataset.local = String(i);
      const local = i;
      cell.addEventListener("click", () => this.clickRefineCell(local));
      this.refineCells.push(cell);
      this.refineGrid.append(cell);
    }
    this.resultGrid = elem(doc, "div", "dragonsoul-result-grid");
    this.resultGrid.setAttribute("role", "grid");
    this.resultGrid.setAttribute("aria-label", "Result slots");
    for (let i = 0; i < RESULT_SLOT_CELLS; i += 1) {
      const cell = doc.createElement("button");
      cell.type = "button";
      cell.className = "dragonsoul-result-cell m2-slot";
      cell.dataset.local = String(i);
      cell.disabled = true;
      this.resultCells.push(cell);
      this.resultGrid.append(cell);
    }
    boards.append(this.refineGrid, this.resultGrid);

    this.stoneLabel = elem(doc, "div", "dragonsoul-section-label", "Refine stone");
    this.stones = elem(doc, "div", "dragonsoul-row dragonsoul-stones");

    this.doRefineButton = doc.createElement("button");
    this.doRefineButton.type = "button";
    // `DO_REFINE` (`locale_interface.txt`, `dragonsoulrefinewindow.py`'s
    // `DoRefineButtonTitle`).
    this.doRefineButton.textContent = "Refine";
    this.doRefineButton.disabled = true;
    this.doRefineButton.addEventListener("click", () => this.refine(this.refineType));

    // `DoRefineButton` (y 200) sits under the two boards (y 39)
    // (`locale/en/ui/dragonsoulrefinewindow.py:67-88,191-195`), so it stays
    // on screen however long the list of picked souls below it grows.
    this.refineRoot.append(
      refineTitle,
      axesRow,
      gridLabel,
      boards,
      this.doRefineButton,
      this.grid,
      this.stoneLabel,
      this.stones,
    );
    container.append(this.refineRoot);
    const onRefineEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || !this.showing) return;
      event.preventDefault();
      event.stopPropagation();
      this.cancel();
    };
    this.refineRoot.addEventListener("keydown", onRefineEscape);
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  /** What the refine buttons would send. A copy. */
  get onGrid(): readonly number[] {
    return [...this.picked];
  }

  /** The bag slot holding the refine stone, or null. */
  get stoneSlot(): number | null {
    return this.stone;
  }

  get canRefine(): boolean {
    return !this.doRefineButton.disabled;
  }

  /** Open, or redraw, from the server's own description of the window. */
  update(souls: DragonSouls, bag: Inventory): void {
    this.frame = souls;
    this.bag = bag;
    // A cell that is no longer occupied cannot stay on the grid: the frame is
    // the truth, and a grid pointing at a soul that was just consumed is the
    // stale-reference bug every other panel here is written to avoid.
    const live = new Set(souls.cells.map((c) => c.cell));
    this.picked = this.picked.filter((cell) => live.has(cell));
    this.redraw();
    this.root.hidden = false;
  }

  /** `OpenDragonSoulRefineWindow` (`interfacemodule.py:1028-1035`): the
   *  refinement window, and the storage window beside it if it was shut. */
  openRefine(souls: DragonSouls, bag: Inventory): void {
    this.update(souls, bag);
    this.refineRoot.hidden = false;
  }

  /** Show what a refinement did. The window redraw follows from the server. */
  settle(outcome: DragonSoulRefined): void {
    this.picked = [];
    this.stone = null;
    this.resultLabel = outcome.succeeded
      ? outcome.result_name
      : outcome.destroyed
        ? ""
        : outcome.result_name;
    this.result.hidden = false;
    this.result.className = `dragonsoul-result ${outcome.succeeded ? "dragonsoul-result-good" : "dragonsoul-result-bad"}`;
    this.result.textContent = describeOutcome(outcome);
    this.redraw();
  }

  private clickStorageCell(local: number, event?: MouseEvent): void {
    if (this.frame === null) return;
    if (!this.askEl.hidden) return;
    if (!Number.isInteger(local) || local < 0 || local >= STORAGE_GRID_CELLS) return;
    const cell = this.kind * STEP_MAX * BOX_SIZE + this.gradePage * BOX_SIZE + local;
    if (this.armed !== null) {
      this.useArmed(cell);
      return;
    }
    // `SelectItemSlot`'s own order once nothing is held: the sell cursor, then
    // Alt, then the pick (`uidragonsoul.py:364-378`).
    if (this.sell !== null) {
      this.sellCell(cell);
      return;
    }
    if (event?.altKey === true) {
      this.linkCell(cell);
      return;
    }
    if (this.frame.cells.some((c) => c.cell === cell)) this.toggle(cell);
  }

  /** `UseItemSlot` (`uidragonsoul.py:476-496`): with the refinement window
   *  open the soul goes onto its grid (`AutoSetItem`); otherwise an expired
   *  soul only says so (`__UseItem`, `:326-331`) and a live one asks before it
   *  is worn in the viewed deck (`__EquipItem`, `:335-341`; `__Accept`, `:614-618`). */
  private useStorageCell(local: number): void {
    if (this.frame === null || !this.askEl.hidden) return;
    if (!Number.isInteger(local) || local < 0 || local >= STORAGE_GRID_CELLS) return;
    const cell = this.kind * STEP_MAX * BOX_SIZE + this.gradePage * BOX_SIZE + local;
    const soul = this.frame.cells.find((c) => c.cell === cell);
    if (soul === undefined) return;
    if (!this.refineRoot.hidden) {
      if (!this.picked.includes(cell)) this.toggle(cell);
      return;
    }
    if (soul.remaining_seconds === 0) {
      this.ask(DRAGON_SOUL_EXPIRED, null);
      return;
    }
    this.ask(`${DRAGON_SOUL_EQUIP_WARNING1}\n${DRAGON_SOUL_EQUIP_WARNING2}`, () => {
      this.move(cell, deckCell(soul, this.viewingDeck));
    });
  }

  /** `__SellItem` (`uidragonsoul.py:382-413`): refuse an item no dealer takes
   *  with the shipped popup, otherwise ask before anything is sent. */
  private sellCell(cell: number): void {
    const dealer = this.sell;
    if (dealer === null || this.frame === null) return;
    const soul = this.frame.cells.find((c) => c.cell === cell);
    if (soul === undefined) return;
    if (soul.sell_value <= 0) {
      this.ask(CANNOT_SELL, null);
      return;
    }
    this.ask(sellQuestion(soul.name, soul.count, soul.sell_value), () => {
      this.actions.onSell?.(dealer.npcId, cell, soul.count);
    });
  }

  /** `ime.PasteString(player.GetItemLink(DRAGON_SOUL_INVENTORY, cell))`
   *  (`uidragonsoul.py:370-372`). A soul's socket 0 is its remaining time
   *  (`ITEM_SOCKET_REMAIN_SEC`, `item.cpp:1415-1470`). The `:%x:%d` attribute
   *  pairs the original appends are `aAttr[i].bType` bytes
   *  (`PythonPlayerModule.cpp:1043-1049`), and `SoulSlot.applies` carries
   *  apply *names*, so a soul's link stops at its sockets. */
  private linkCell(cell: number): void {
    const soul = this.frame?.cells.find((c) => c.cell === cell);
    if (soul === undefined) return;
    this.actions.onItemLink?.(
      itemLink(soul.vnum, soul.name, { socket: soul.remaining_seconds }),
    );
  }

  /** Open the QuestionDialog. A null `accept` is `PopupDialog`: one way out
   *  and nothing sent (`uidragonsoul.py:391-396,430-432`). */
  private ask(text: string, accept: (() => void) | null): void {
    this.askText.textContent = text;
    this.pendingAsk = accept;
    this.askEl.hidden = false;
    this.askYes.focus();
  }

  private acceptAsk(): void {
    const accept = this.pendingAsk;
    this.closeAsk();
    accept?.();
  }

  /** `OnCloseQuestionDialog`: close, send nothing (`uidragonsoul.py:423-427`). */
  private closeAsk(): void {
    this.pendingAsk = null;
    this.askEl.hidden = true;
  }

  /** The question on screen, or "" when there is none. */
  get asking(): string {
    return this.askEl.hidden ? "" : (this.askText.textContent ?? "");
  }

  /** Answer the open question the way the Yes button does. */
  answerAsk(): void {
    if (this.askEl.hidden) return;
    this.acceptAsk();
  }

  enterSellMode(npcId: number, dealerName: string): void {
    this.sell = { npcId, dealerName };
    this.picked = [];
    this.redraw();
  }

  exitSellMode(): void {
    if (this.sell === null) return;
    this.sell = null;
    this.closeAsk();
    this.redraw();
  }

  /** The dealer this window would sell to, or null. */
  get sellingTo(): number | null {
    return this.sell?.npcId ?? null;
  }

  /** Click a filled `RefineSlot` cell to take that soul back off the grid. */
  private clickRefineCell(local: number): void {
    const cell = this.picked[local];
    if (cell === undefined) return;
    this.toggle(cell);
  }

  /** Put a stored soul on the refinement grid, or take it off again. */
  toggle(cell: number): void {
    if (this.frame === null || isDeckCell(cell)) return;
    const at = this.picked.indexOf(cell);
    if (at >= 0) this.picked.splice(at, 1);
    else {
      // Cap at the RefineSlot table so a long pick list cannot outgrow the
      // 5×3 face the script draws (`dragonsoulrefinewindow.py:67-80`).
      if (this.picked.length >= REFINE_SLOT_CELLS) return;
      this.picked.push(cell);
    }
    this.redraw();
  }

  /** Choose which bag slot holds the refine stone. Clicking it again clears. */
  chooseStone(slot: number): void {
    this.stone = this.stone === slot ? null : slot;
    this.redraw();
  }

  /** What the inventory currently has armed, or null. A copy of a label and a
   *  cell; this panel never reads the bag itself for it. */
  get armedBagSlot(): number | null {
    return this.armed?.slot ?? null;
  }

  armBagSlot(slot: number | null, name: string): void {
    const next = slot === null ? null : { slot, name };
    if (next === null && this.armed === null) return;
    this.armed = next;
    this.redraw();
  }

  useArmed(cell: number): void {
    const armed = this.armed;
    if (armed === null || this.frame === null) return;
    this.armed = null;
    this.redraw();
    this.actions.onUseFromBag?.(armed.slot, DRAGON_SOUL_SLOT_BASE + cell);
  }

  /** Wear a stored soul, or take a worn one off. Both are one move. */
  move(source: number, dest: number): void {
    this.actions.onMove?.(source, dest);
  }

  showDeck(which: number): void {
    if (!Number.isInteger(which) || which < 0 || which >= DECK_COUNT) return;
    if (which === this.viewingDeck) return;
    this.viewingDeck = which;
    this.redraw();
  }

  /** Switch the *viewed* deck on. There is no "off": see `DragonSoulActions.onDeck`. */
  activateDeck(): void {
    this.actions.onDeck?.(this.viewingDeck);
  }

  /** Select a storage kind. This only changes what is shown; the server owns
   * all cells and moves. `SetDSKindIndex` clears the former page's highlights
   * before showing the requested tab (`uidragonsoul.py:628-644`). */
  private setKind(kind: number): void {
    if (!Number.isInteger(kind) || kind < 0 || kind >= SOUL_KIND_TITLES.length) return;
    if (kind === this.kind) return;
    this.kind = kind;
    this.picked = [];
    this.redraw();
  }

  /** Select a grade page inside the current kind, the nested tab set
   *  `Inventory_Tab_01`..`_05` name (`dragonsoulwindow.py:44-186`).
   *  `SetInventoryPage` clears the former page's highlights the same way
   *  `SetDSKindIndex` does (`uidragonsoul.py:201-209`). */
  private setGrade(grade: number): void {
    if (!Number.isInteger(grade) || grade < 0 || grade >= GRADE_TITLES.length) return;
    if (grade === this.gradePage) return;
    this.gradePage = grade;
    this.picked = [];
    this.redraw();
  }

  chooseRefineType(operation: number): void {
    if (operation === this.refineType) return;
    this.refineType = operation;
    this.picked = [];
    this.stone = null;
    this.redraw();
  }

  refine(operation: number): void {
    if (this.frame === null || this.picked.length === 0) return;
    if (operation === REFINE_STRENGTH && this.stone === null) return;
    const souls = [...this.picked];
    const stones = operation === REFINE_STRENGTH && this.stone !== null ? [this.stone] : [];
    // Cleared before the send, so a double click is one attempt. The server
    // would refuse the second -- the souls are spent by then -- but a window
    // that lets a player ask twice looks like it charged them twice.
    this.picked = [];
    this.redraw();
    this.actions.onRefine?.(operation, souls, stones);
  }

  close(): void {
    this.root.hidden = true;
    this.refineRoot.hidden = true;
    this.exitSellMode();
    this.closeAsk();
    this.frame = null;
    this.bag = null;
    this.picked = [];
    this.stone = null;
    this.armed = null;
    this.resultLabel = null;
    this.armedHint.hidden = true;
    this.result.hidden = true;
    this.stored.replaceChildren();
    this.deckRow.replaceChildren();
    this.deckButtons.replaceChildren();
    this.grid.replaceChildren();
    this.stones.replaceChildren();
    for (const cell of this.storageCells) {
      cell.replaceChildren();
      cell.removeAttribute("title");
      cell.dataset.occupied = "false";
      cell.classList.remove("dragonsoul-soul-picked");
    }
    for (const cell of this.refineCells) {
      cell.replaceChildren();
      cell.removeAttribute("title");
      cell.dataset.occupied = "false";
    }
    for (const cell of this.resultCells) {
      cell.replaceChildren();
      cell.removeAttribute("title");
    }
  }

  cancel(): void {
    const refineWasOpen = this.showing;
    this.close();
    if (refineWasOpen) this.actions.onRefine?.(REFINE_CLOSE, [], []);
    this.actions.onCancel?.();
  }

  private redraw(): void {
    const doc = this.root.ownerDocument;
    const frame = this.frame;
    if (frame === null) return;

    // Drawn from the frame, never assumed. An unqualified character can arrange
    // souls all day and switch none of them on, and a window that did not say
    // why would be a window with a button that silently does nothing.
    this.warning.hidden = frame.qualified;
    // `DRAGON_SOUL_UNQUALIFIED` (`locale_game.txt:94`), verbatim.
    this.warning.textContent = frame.qualified
      ? ""
      : "You cannot yet use Dragon Stone alchemy.";

    // Sell mode owns the hint line while it is on: a click means "sell this"
    // and nothing else (`uidragonsoul.py:366-367`).
    this.root.classList.toggle("dragonsoul-selling", this.sell !== null);
    this.armedHint.hidden = this.armed === null && this.sell === null;
    this.armedHint.textContent =
      this.sell !== null
        ? `Click a Dragon Stone to sell it to ${this.sell.dealerName}.`
        : this.armed === null
          ? ""
          : `Choose a Dragon Stone to use ${this.armed.name} on.`;

    // Only the currently-viewed deck's six cells, not both decks merged --
    // `RefreshEquipSlotWindow` reads `deckPageIndex * DRAGON_SOUL_EQUIPMENT_FIRST_SIZE`
    // (`uidragonsoul.py:218-223,249-253`), one deck's slots at a time.
    const deckStart = DECK_BASE + this.viewingDeck * DECK_SIZE;
    const deckEnd = deckStart + DECK_SIZE;
    const worn = frame.cells.filter((c) => c.cell >= deckStart && c.cell < deckEnd);
    const kept = frame.cells.filter(
      (c) => !isDeckCell(c.cell) && soulKind(c) === this.kind && c.grade === this.gradePage,
    );

    this.deckRow.replaceChildren();
    if (worn.length === 0) {
      const none = elem(doc, "div", "dragonsoul-empty", "Nothing worn.");
      this.deckRow.append(none);
    }
    for (const soul of worn) {
      this.deckRow.append(this.soulRow(doc, soul, { picked: false }));
    }

    this.deckButtons.replaceChildren();
    for (let which = 0; which < DECK_COUNT; which += 1) {
      const button = doc.createElement("button");
      button.type = "button";
      button.className = "dragonsoul-deck-tab";
      button.textContent = `Deck ${which + 1}`;
      if (frame.active_deck === which) button.classList.add("dragonsoul-deck-on");
      button.setAttribute("role", "tab");
      button.setAttribute("aria-selected", String(this.viewingDeck === which));
      button.addEventListener("click", () => this.showDeck(which));
      this.deckButtons.append(button);
    }
    this.deckButtons.append(this.activateButton);
    // `ActivateButtonClick` (`uidragonsoul.py:673-682`): there is no "off" a
    // client can request (see `DragonSoulActions.onDeck`), so once the viewed
    // deck is already the active one the button has nothing left to ask for.
    const viewedIsActive = frame.active_deck === this.viewingDeck;
    this.activateButton.textContent = viewedIsActive
      ? "Active"
      : `Activate deck ${this.viewingDeck + 1}`;
    this.activateButton.disabled = viewedIsActive;

    this.stored.replaceChildren();
    for (const tab of this.kindTabs.querySelectorAll<HTMLButtonElement>(".dragonsoul-kind-tab")) {
      tab.setAttribute("aria-selected", String(tab.dataset.soulKind === String(this.kind)));
    }
    for (const tab of this.gradeTabs.querySelectorAll<HTMLButtonElement>(".dragonsoul-grade-tab")) {
      tab.setAttribute("aria-selected", String(tab.dataset.soulGrade === String(this.gradePage)));
    }

    // Paint the 8×4 `ItemSlot` for this (kind, grade) page.
    const pageStart = this.kind * STEP_MAX * BOX_SIZE + this.gradePage * BOX_SIZE;
    const keptByCell = new Map(kept.map((s) => [s.cell, s]));
    for (let i = 0; i < STORAGE_GRID_CELLS; i += 1) {
      const button = this.storageCells[i]!;
      const cell = pageStart + i;
      const soul = keptByCell.get(cell);
      button.replaceChildren();
      button.classList.toggle("dragonsoul-soul-picked", soul !== undefined && this.picked.includes(soul.cell));
      if (soul === undefined) {
        button.dataset.occupied = "false";
        button.removeAttribute("title");
        continue;
      }
      button.dataset.occupied = "true";
      button.title = soul.name;
      const icon = itemIconNode(soul.vnum, doc);
      if (icon !== null) button.append(icon);
      else {
        const glyph = elem(doc, "span", "dragonsoul-storage-glyph", soul.name.slice(0, 3));
        button.append(glyph);
      }
    }

    if (kept.length === 0) {
      const none = elem(doc, "div", "dragonsoul-empty", "No dragon souls.");
      this.stored.append(none);
    }
    for (const soul of kept) {
      this.stored.append(
        this.soulRow(doc, soul, { picked: this.picked.includes(soul.cell) }),
      );
    }

    // Paint RefineSlot / ResultSlot grids.
    for (let i = 0; i < REFINE_SLOT_CELLS; i += 1) {
      const button = this.refineCells[i]!;
      const cell = this.picked[i];
      button.replaceChildren();
      if (cell === undefined) {
        button.dataset.occupied = "false";
        button.removeAttribute("title");
        continue;
      }
      const soul = frame.cells.find((c) => c.cell === cell);
      button.dataset.occupied = "true";
      button.title = soul?.name ?? `Cell ${cell}`;
      if (soul !== undefined) {
        const icon = itemIconNode(soul.vnum, doc);
        if (icon !== null) button.append(icon);
        else {
          const glyph = elem(doc, "span", "dragonsoul-refine-glyph", soul.name.slice(0, 3));
          button.append(glyph);
        }
      }
    }
    for (let i = 0; i < RESULT_SLOT_CELLS; i += 1) {
      const button = this.resultCells[i]!;
      button.replaceChildren();
      if (i === 0 && this.resultLabel) {
        button.title = this.resultLabel;
        const glyph = elem(doc, "span", "dragonsoul-result-glyph", this.resultLabel.slice(0, 3));
        button.append(glyph);
      } else {
        button.removeAttribute("title");
      }
    }

    this.grid.replaceChildren();
    if (this.picked.length === 0) {
      const none = elem(doc, "span", "dragonsoul-empty", "Nothing on the grid.");
      this.grid.append(none);
    }
    for (const cell of this.picked) {
      const soul = frame.cells.find((c) => c.cell === cell);
      const button = doc.createElement("button");
      button.type = "button";
      button.textContent = soul?.name ?? `Cell ${cell}`;
      button.addEventListener("click", () => this.toggle(cell));
      this.grid.append(button);
    }

    this.stones.hidden = this.refineType !== REFINE_STRENGTH;
    this.stoneLabel.hidden = this.stones.hidden;
    this.stones.replaceChildren();
    for (const slot of this.bag?.slots ?? []) {
      const button = doc.createElement("button");
      button.type = "button";
      button.textContent =
        this.stone === slot.slot ? `${slot.name} (stone)` : slot.name;
      button.addEventListener("click", () => this.chooseStone(slot.slot));
      this.stones.append(button);
    }

    for (const { button, operation } of this.refineAxisButtons) {
      const on = operation === this.refineType;
      button.classList.toggle("dragonsoul-refine-axis-on", on);
      button.setAttribute("aria-selected", String(on));
    }
    const any = this.picked.length > 0;
    // Strength takes exactly one soul and exactly one stone, and the original
    // treats a second of either as a hostile client rather than as a mistake
    // (`DragonSoul.cpp:880-897`). Greyed here so a player is not told that.
    this.doRefineButton.disabled =
      !any ||
      (this.refineType === REFINE_STRENGTH && (this.picked.length !== 1 || this.stone === null));
  }

  private soulRow(
    doc: Document,
    soul: SoulSlot,
    options: { picked: boolean },
  ): HTMLElement {
    const row = elem(doc, "div", "dragonsoul-soul");
    if (soul.active) row.classList.add("dragonsoul-soul-active");
    if (options.picked) row.classList.add("dragonsoul-soul-picked");

    const head = elem(doc, "div", "dragonsoul-soul-head");
    const name = elem(
      doc,
      "span",
      "dragonsoul-name",
      soul.count > 1 ? `${soul.name} x${soul.count}` : soul.name,
    );
    const clock = doc.createElement("span");
    clock.textContent = formatRemaining(soul.remaining_seconds);
    head.append(name, clock);
    row.append(head);

    const applies = elem(doc, "div", "dragonsoul-applies");
    for (const apply of soul.applies) {
      const line = doc.createElement("div");
      line.className = apply.basic
        ? "dragonsoul-apply-basic"
        : "dragonsoul-apply-drawn";
      line.textContent = `${apply.apply} +${apply.value}`;
      applies.append(line);
    }
    row.append(applies);

    const controls = elem(doc, "div", "dragonsoul-row");
    if (this.armed !== null) {
      const spend = doc.createElement("button");
      spend.type = "button";
      spend.className = "dragonsoul-use-armed";
      spend.textContent = `Use ${this.armed.name}`;
      spend.addEventListener("click", () => this.useArmed(soul.cell));
      controls.append(spend);
    }
    if (isDeckCell(soul.cell)) {
      const off = doc.createElement("button");
      off.type = "button";
      off.textContent = "Prise out (may destroy)";
      off.addEventListener("click", () => this.move(soul.cell, boxStart(soul)));
      controls.append(off);
    } else {
      for (let deck = 0; deck < DECK_COUNT; deck += 1) {
        const wear = doc.createElement("button");
        wear.type = "button";
        wear.textContent = `Wear in deck ${deck + 1}`;
        wear.addEventListener("click", () => this.move(soul.cell, deckCell(soul, deck)));
        controls.append(wear);
      }
      const pick = doc.createElement("button");
      pick.type = "button";
      pick.textContent = options.picked ? "Off the grid" : "On the grid";
      pick.addEventListener("click", () => this.toggle(soul.cell));
      controls.append(pick);
    }
    row.append(controls);
    return row;
  }
}
